import { surfaceName } from '../terrain/heightfield.js';
import { CAM_LABELS } from './camera.js';

export class HUD {
  constructor(checkpoints, trackLoop) {
    this.checkpoints = checkpoints;
    this.trackLoop = trackLoop;

    // DOM References
    this.elSpeed = document.getElementById('speed');
    this.elGear = document.getElementById('gear');
    this.elSpeedBar = document.querySelector('#speedBar i');
    this.elRpmVal = document.getElementById('rpm-val');
    this.elRpmBar = document.querySelector('#rpmBar i');
    this.elBoostVal = document.getElementById('boost-val');
    this.elBoostBar = document.querySelector('#boostBar i');
    this.elSurface = document.getElementById('surface-val');
    this.elSlip = document.getElementById('slip-val');
    this.elAlt = document.getElementById('alt-val');

    this.elTime = document.getElementById('current-time');
    this.elLap = document.getElementById('lap-count');
    this.elBestLap = document.getElementById('best-lap');
    this.elCpInfo = document.getElementById('checkpoint-info');
    this.elWrongWay = document.getElementById('wrong-way');
    this.elCamBadge = document.getElementById('cam-badge');
    this.elBanner = document.getElementById('banner-notify');

    // Minimap
    this.minimapCanvas = document.getElementById('minimap-canvas');
    this.minimapCtx = this.minimapCanvas?.getContext('2d');
    this.mapScale = 0.22; // ~650m world mapped into ~145px
    this.mapCenter = 80;

    this.bannerTimeout = null;
  }

  showNotification(title, subtitle = '') {
    if (!this.elBanner) return;
    this.elBanner.innerHTML = `${title}${subtitle ? `<small>${subtitle}</small>` : ''}`;
    this.elBanner.classList.add('show');
    clearTimeout(this.bannerTimeout);
    this.bannerTimeout = setTimeout(() => {
      this.elBanner.classList.remove('show');
    }, 2800);
  }

  setCameraMode(mode) {
    if (this.elCamBadge) {
      this.elCamBadge.textContent = `КАМЕРА: ${CAM_LABELS[mode] || mode}`;
    }
  }

  setBackend(name) {
    const el = document.getElementById('backend-name');
    if (el) el.textContent = name;
  }

  update(jeep, raceDirector) {
    // 1. Speed & Gear
    const kmh = Math.round(jeep.speedKmh);
    if (this.elSpeed) this.elSpeed.textContent = `${kmh}`;
    if (this.elSpeedBar) {
      const pct = Math.min(100, Math.round((kmh / 160) * 100));
      this.elSpeedBar.style.width = `${pct}%`;
    }

    if (this.elGear) {
      if (jeep.gear === -1) this.elGear.textContent = 'R';
      else if (jeep.gear === 0) this.elGear.textContent = 'N';
      else this.elGear.textContent = `${jeep.gear}`;
    }

    // 2. RPM & Boost
    if (this.elRpmVal) this.elRpmVal.textContent = `${Math.round(jeep.rpm)}`;
    if (this.elRpmBar) {
      const rpmPct = Math.min(100, Math.max(0, Math.round(((jeep.rpm - 900) / 6300) * 100)));
      this.elRpmBar.style.width = `${rpmPct}%`;
    }

    const boostPct = Math.round(jeep.boost * 100);
    if (this.elBoostVal) this.elBoostVal.textContent = `${boostPct}%`;
    if (this.elBoostBar) this.elBoostBar.style.width = `${boostPct}%`;

    // 3. Surface & Slip
    if (this.elSurface) this.elSurface.textContent = surfaceName(jeep.surface);
    if (this.elSlip) this.elSlip.textContent = `${Math.round(jeep.slip * 100)}%`;
    if (this.elAlt) this.elAlt.textContent = `${Math.round(jeep.pos.y)} м`;

    // 4. Race progression
    if (this.elTime) this.elTime.textContent = raceDirector.formatTime(raceDirector.lapTime);
    if (this.elLap) {
      this.elLap.textContent = `КРУГ ${raceDirector.currentLap} / ${raceDirector.totalLaps}`;
    }
    if (this.elBestLap) {
      this.elBestLap.textContent = `ЛУЧШИЙ: ${raceDirector.formatTime(raceDirector.bestLapTime)}`;
    }
    if (this.elCpInfo) {
      this.elCpInfo.textContent = `ЧЕКПОИНТ ${raceDirector.activeCpIndex} / ${raceDirector.cpCount}`;
    }

    if (this.elWrongWay) {
      this.elWrongWay.style.display = raceDirector.wrongWay ? 'block' : 'none';
    }

    // 5. Minimap Render
    this.renderMinimap(jeep, raceDirector);
  }

  renderMinimap(jeep, raceDirector) {
    const ctx = this.minimapCtx;
    if (!ctx) return;

    ctx.clearRect(0, 0, 160, 160);

    const worldToMap = (wx, wz) => {
      return {
        x: this.mapCenter + wx * this.mapScale,
        y: this.mapCenter + wz * this.mapScale
      };
    };

    // Draw Track Polyline
    ctx.beginPath();
    const loop = this.trackLoop;
    for (let i = 0; i < loop.length; i++) {
      const p = worldToMap(loop[i][0], loop[i][1]);
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();

    ctx.strokeStyle = '#64748b';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Draw Checkpoints
    for (let i = 0; i < this.checkpoints.length; i++) {
      const cp = this.checkpoints[i];
      const m = worldToMap(cp.x, cp.z);
      ctx.beginPath();
      ctx.arc(m.x, m.y, i === raceDirector.activeCpIndex ? 4.5 : 2.5, 0, Math.PI * 2);

      if (i === raceDirector.activeCpIndex) {
        ctx.fillStyle = '#f59e0b';
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      } else if (i < raceDirector.activeCpIndex) {
        ctx.fillStyle = '#06b6d4';
        ctx.fill();
      } else {
        ctx.fillStyle = '#475569';
        ctx.fill();
      }
    }

    // Draw Player Jeep marker
    const p = worldToMap(jeep.pos.x, jeep.pos.z);
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(jeep.yaw);

    // Directional arrow
    ctx.beginPath();
    ctx.moveTo(0, -6.5);
    ctx.lineTo(4.5, 4.5);
    ctx.lineTo(0, 2.5);
    ctx.lineTo(-4.5, 4.5);
    ctx.closePath();
    ctx.fillStyle = '#ef4444';
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
  }
}
